import type { GameAction, MatchView, ReactionId, SeatIndex } from "@meksiko/shared-types";
import { Client, Room } from "colyseus.js";

// Mirrors MatchView.turnDeadline/turnTimeoutMs so the waiting-room loader can
// show a real countdown the same way the in-match turn timer does.
export interface WaitingRoomInfo {
  seatsFilled: number;
  seatsTotal: number;
  waitingDeadline: number | null;
  waitingTimeoutMs: number;
}

export interface ConnectionHandlers {
  onState(view: MatchView): void;
  onWaiting(info: WaitingRoomInfo): void;
  onActionRejected(code: string): void;
  onMatchStartFailed(reason: string): void;
  onReaction(seat: SeatIndex, reactionId: ReactionId): void;
  onDisconnected(): void;
}

export class GameConnection {
  private client: Client;
  private room: Room | null = null;

  constructor(wsBaseUrl: string) {
    this.client = new Client(wsBaseUrl);
  }

  /**
   * `activeMatchId` (from GET /api/me — see appStore.ts's bootstrap/joinQueue)
   * routes the player back into a match they already have a seat in, via
   * joinById, instead of matchmaking into a brand-new room. Falls back to a
   * normal joinOrCreate if that fails (e.g. the match ended in the meantime
   * and the id is stale).
   */
  async join(sessionToken: string, handlers: ConnectionHandlers, activeMatchId?: string | null): Promise<void> {
    let room: Room;
    if (activeMatchId) {
      try {
        room = await this.client.joinById(activeMatchId, { token: sessionToken });
      } catch {
        room = await this.client.joinOrCreate("meksiko", { token: sessionToken });
      }
    } else {
      room = await this.client.joinOrCreate("meksiko", { token: sessionToken });
    }

    this.room = room;

    room.onMessage("state", (view: MatchView) => handlers.onState(view));
    room.onMessage("waiting", handlers.onWaiting);
    room.onMessage("actionRejected", (msg: { code: string }) => handlers.onActionRejected(msg.code));
    room.onMessage("matchStartFailed", (msg: { reason: string }) => handlers.onMatchStartFailed(msg.reason));
    room.onMessage("reaction", (msg: { seat: SeatIndex; reactionId: ReactionId }) => handlers.onReaction(msg.seat, msg.reactionId));
    room.onLeave(() => handlers.onDisconnected());
  }

  sendAction(action: GameAction): void {
    this.room?.send("action", action);
  }

  /** GAME_SPEC.md §10 — tells the server the player is back after an AI takeover due to a timeout. */
  sendResumeControl(): void {
    this.room?.send("resumeControl");
  }

  sendReaction(reactionId: ReactionId): void {
    this.room?.send("reaction", { reactionId });
  }

  leaveMatch(): void {
    this.room?.send("leaveMatch");
  }

  disconnect(): void {
    this.room?.leave();
    this.room = null;
  }
}
